import React, { useState } from "react";
import * as PropTypes from "prop-types";
import { chartImageScaleFactor } from "../../constants";
import { downloadAnnotatedCsv, serializeSettings } from "../../data/csv";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/styles";
import styleConstants from "../../styleConstants";
import Box from "../box/Box";
import DownloadIcon from "./DownloadIcon";
import DownloadMenu from "./DownloadMenu";

const useStyles = makeStyles({
  absoluteToggle: {
    marginLeft: props => (props.filters ? "2rem" : undefined)
  },
  container: {
    width: props => props.width,
    backgroundColor: "white",
    paddingBottom: "2rem"
  },
  contentContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center"
  },
  controls: {
    marginTop: "2rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  headingContainer: {
    display: "flex",
    justifyContent: "space-between"
  },
  dataViewer: {
    width: props => props.contentWidth
  },
  dataViewerContainer: {
    display: "flex",
    justifyContent: "center",
    overflowX: "auto"
  },
  filter: {
    marginTop: "1rem"
  },
  filters: {
    flex: 1
  },
  header: {
    borderTop: `2px solid ${styleConstants.colors.themeBlue}`,
    marginBottom: "2rem"
  },
  reference: {
    display: "inline-block",
    backgroundColor: styleConstants.colors.themeBlue,
    padding: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: styleConstants.fontWeights.bold,
    textTransform: "uppercase",
    color: "white"
  },
  title: {
    fontSize: "1rem",
    fontWeight: styleConstants.fontWeights.bold
  },
  subtitle: {
    fontSize: "1rem",
    fontWeight: styleConstants.fontWeights.book,
    fontStyle: "italic",
    color: styleConstants.colors.dark
  },
  button: {
    marginTop: "-12px"
  }
});

const DataViewerFrame = props => {
  const [downloadMenuAnchorElement, setDownloadMenuAnchorElement] = useState();

  const classes = useStyles(props);

  const handleClose = () => {
    setDownloadMenuAnchorElement(undefined);
  };

  return (
    <Box
      width={props.width}
      backgroundColor="white"
      reference={props.reference}
    >
      <div className={classes.headingContainer}>
        <div className={classes.titleContainer}>
          <h4 className="figure-title">{props.title}</h4>
          <h4 className="figure-subtitle">{props.subtitle}</h4>
        </div>
        <div>
          <IconButton
            className={classes.button}
            aria-label="download"
            disableFocusRipple={true}
            edge="end"
            onClick={e => {
              setDownloadMenuAnchorElement(e.currentTarget);
            }}
          >
            <DownloadIcon />
          </IconButton>

          <DownloadMenu
            anchorElement={downloadMenuAnchorElement}
            open={!!downloadMenuAnchorElement}
            onDownloadCsv={
              (props.onDownloadCsv || props.chartRef) &&
              (() => {
                if (props.onDownloadCsv) {
                  props.onDownloadCsv();
                } else if (props.chartRef) {
                  let csv = props.chartRef.current.chart.getCSV();

                  if (props.downloadAbsoluteValues) {
                    csv = csv.replace(/,-/g, ",");
                  }

                  if (props.chartSettings) {
                    csv += serializeSettings(props.chartSettings, true);
                  }

                  downloadAnnotatedCsv(
                    csv,
                    `${props.dataViewerId || "chart"}.csv`
                  );
                }

                handleClose();
              })
            }
            onDownloadPng={
              (props.onDownloadPng || props.chartRef) &&
              (() => {
                if (props.onDownloadPng) {
                  props.onDownloadPng();
                } else {
                  props.chartRef.current.chart.exportChart({
                    type: "image/png",
                    filename: `${props.dataViewerId || "chart"}`,
                    scale: chartImageScaleFactor
                  });
                }

                handleClose();
              })
            }
            onClose={() => {
              handleClose();
            }}
          />
        </div>
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.dataViewerContainer}>
          <div className={classes.dataViewer}>{props.children}</div>
        </div>
      </div>
      {(props.filters || props.absoluteToggle) && (
        <div className={classes.controls}>
          <div className={classes.filters}>
            {props.filters &&
              props.filters.map((filter, index) => (
                <div
                  key={filter.props.id}
                  className={index ? classes.filter : undefined}
                >
                  {filter}
                </div>
              ))}
          </div>
          {props.absoluteToggle && (
            <div className={classes.absoluteToggle}>{props.absoluteToggle}</div>
          )}
        </div>
      )}
    </Box>
  );
};

DataViewerFrame.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataViewerId: PropTypes.string,
  reference: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  contentWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.element.isRequired,
  filters: PropTypes.arrayOf(PropTypes.element),
  absoluteToggle: PropTypes.element,
  chartRef: PropTypes.object,
  chartSettings: PropTypes.object,
  downloadAbsoluteValues: PropTypes.bool,
  onDownloadCsv: PropTypes.func,
  onDownloadPng: PropTypes.func
};

DataViewerFrame.defaultProps = {
  contentWidth: "100%",
  downloadAbsoluteValues: false
};

export default DataViewerFrame;
